import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { fetchFeaturedByFormat } from '../../services/buylistService';
import { isMockMode } from '../../services/mockData';
import { addToBuylist } from '../../redux/buylistSlice';
import { BuylistItem } from '../../types';
import { theme, media } from '../../styles/theme';
import { AnimatedSection, StaggerContainer, StaggerItem } from '../common/AnimatedSection';
import BuylistItemCard from './BuylistItemCard';

const FeaturedSection = styled.section`
  background-color: ${theme.colors.gray.lightest};
  padding: ${theme.spacing.lg} 0;
  margin-bottom: ${theme.spacing.lg};
`;

const FeaturedContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;

  ${media.mobile} {
    padding: 0 1rem;
  }
`;

const FeaturedHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.sm};
`;

const FeaturedTitle = styled.h2`
  font-size: ${theme.fontSizes.xxl};
  font-weight: ${theme.fontWeights.medium};
  color: ${theme.colors.text};
`;

const FeaturedSubtitle = styled.p`
  color: ${theme.colors.gray.dark};
  margin-bottom: ${theme.spacing.md};
`;

const SeeAllLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: ${theme.colors.primary};
  text-decoration: none;
  font-weight: ${theme.fontWeights.medium};
  transition: color ${theme.transitions.fast};

  &:hover {
    color: ${theme.colors.accent};
  }
`;

const ScrollWrapper = styled.div`
  position: relative;
`;

const ScrollIndicator = styled.div<{ $direction: 'left' | 'right'; $visible: boolean }>`
  display: none;
  position: absolute;
  top: 0;
  bottom: ${theme.spacing.sm};
  width: 2.5rem;
  z-index: 2;
  pointer-events: none;
  opacity: ${props => props.$visible ? 1 : 0};
  transition: opacity ${theme.transitions.fast};
  ${props => props.$direction === 'left'
    ? `left: 0; background: linear-gradient(to right, ${theme.colors.gray.lightest}, transparent);`
    : `right: 0; background: linear-gradient(to left, ${theme.colors.gray.lightest}, transparent);`}

  &::after {
    content: '${props => props.$direction === 'left' ? '\\2039' : '\\203A'}';
    position: absolute;
    top: 50%;
    ${props => props.$direction === 'left' ? 'left: 0.25rem;' : 'right: 0.25rem;'}
    transform: translateY(-50%);
    font-size: 1.5rem;
    color: ${theme.colors.gray.dark};
  }

  ${media.mobile} {
    display: block;
  }
`;

const ScrollContainer = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;
  padding-bottom: ${theme.spacing.sm};

  /* Hide scrollbar */
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const NavButton = styled.button<{ $direction: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${props => props.$direction === 'left' ? 'left: -1rem;' : 'right: -1rem;'}
  z-index: 2;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: ${theme.borderRadius.full};
  border: none;
  background-color: ${theme.colors.secondary};
  color: ${theme.colors.white};
  font-size: 1.25rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${theme.shadows.medium};
  transition: opacity ${theme.transitions.fast}, background-color ${theme.transitions.fast};

  &:hover {
    background-color: ${theme.colors.primary};
  }

  &:disabled {
    opacity: 0;
    pointer-events: none;
  }

  ${media.mobile} {
    display: none;
  }
`;

const ScrollItem = styled.div`
  display: flex;
  width: 100%;

  /* Ensure card fills full width and height */
  & > * {
    flex: 1;
    min-height: 0;
  }
`;

const GridStaggerItem = styled(StaggerItem)`
  /* 5 cards per row: account for 4 gaps of 1.5rem between 5 items */
  flex: 0 0 calc(20% - 1.2rem);
  scroll-snap-align: start;
  display: flex;

  ${media.desktop} {
    flex: 0 0 280px;
  }

  ${media.tablet} {
    flex: 0 0 260px;
  }

  ${media.mobile} {
    flex: 0 0 calc(100% - 1rem);
    scroll-snap-align: center;
  }
`;

const LoadingText = styled.div`
  text-align: center;
  padding: ${theme.spacing.xl};
  color: ${theme.colors.gray.dark};
`;

const MOCK_FEATURED_CARDS: BuylistItem[] = [
  { buylistID: 1, cardBlueprintId: 101, blueprintName: 'Tom Bombadil', blueprintVersion: 'Borderless Poster', expansionName: 'The Lord of the Rings Holiday Release', foil: true, maxQuantity: 2, buyPrice: 1932.18, buylistStoreCreditPrice: 2125.40, image_url: '' },
  { buylistID: 2, cardBlueprintId: 102, blueprintName: 'Fire Lord Zuko', blueprintVersion: 'Neon Ink', expansionName: 'Avatar: The Last Airbender Collectors', foil: false, maxQuantity: 3, buyPrice: 1885.62, buylistStoreCreditPrice: 2074.18, image_url: '' },
  { buylistID: 3, cardBlueprintId: 103, blueprintName: 'Sauron, the Dark Lord', blueprintVersion: 'Borderless Poster', expansionName: 'The Lord of the Rings Holiday Release', foil: false, maxQuantity: 2, buyPrice: 1838.77, buylistStoreCreditPrice: 2022.64, image_url: '' },
  { buylistID: 4, cardBlueprintId: 104, blueprintName: 'Iroh, Grand Lotus', blueprintVersion: '', expansionName: 'Avatar: The Last Airbender Promos', foil: false, maxQuantity: 3, buyPrice: 1733.32, buylistStoreCreditPrice: 1906.65, image_url: '' },
  { buylistID: 5, cardBlueprintId: 105, blueprintName: 'Swords to Plowshares', blueprintVersion: '', expansionName: 'Friday Night Magic', foil: false, maxQuantity: 8, buyPrice: 1921.81, buylistStoreCreditPrice: 2113.99, image_url: '' },
  { buylistID: 6, cardBlueprintId: 106, blueprintName: 'Swords to Plowshares', blueprintVersion: '', expansionName: 'MagicFest', foil: false, maxQuantity: 8, buyPrice: 1812.73, buylistStoreCreditPrice: 1994.00, image_url: '' },
  { buylistID: 7, cardBlueprintId: 107, blueprintName: 'Rhystic Study', blueprintVersion: 'Anime Borderless', expansionName: 'Wilds of Eldraine Enchanting Tales', foil: true, maxQuantity: 8, buyPrice: 1652.96, buylistStoreCreditPrice: 1818.26, image_url: '' },
  { buylistID: 8, cardBlueprintId: 108, blueprintName: 'Mana Crypt', blueprintVersion: 'Borderless', expansionName: 'Double Masters 2022', foil: false, maxQuantity: 4, buyPrice: 1590.00, buylistStoreCreditPrice: 1749.00, image_url: '' },
  { buylistID: 9, cardBlueprintId: 109, blueprintName: 'Force of Will', blueprintVersion: '', expansionName: 'Alliances', foil: false, maxQuantity: 5, buyPrice: 1475.50, buylistStoreCreditPrice: 1623.05, image_url: '' },
  { buylistID: 10, cardBlueprintId: 110, blueprintName: 'Cyclonic Rift', blueprintVersion: 'Extended Art', expansionName: 'Commander Masters', foil: false, maxQuantity: 6, buyPrice: 1320.00, buylistStoreCreditPrice: 1452.00, image_url: '' },
];

const FeaturedCards: React.FC = () => {
  const [allCards, setAllCards] = useState<BuylistItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [quantities, setQuantities] = useState<Record<number, number>>({});
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();

  const updateScrollButtons = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  const scroll = useCallback((direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const scrollAmount = el.clientWidth * 0.8;
    el.scrollBy({ left: direction === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const loadAll = async () => {
      try {
        setLoading(true);
        const [commanders, edhCards, premodern, modern] = await Promise.all([
          fetchFeaturedByFormat('commander'),
          fetchFeaturedByFormat('edhcard'),
          fetchFeaturedByFormat('premodern'),
          fetchFeaturedByFormat('modern'),
        ]);

        // Flatten all categories into one list, deduplicate by cardBlueprintId
        // so foil and non-foil variants of the same card don't both appear
        const combined = [...commanders, ...edhCards, ...premodern, ...modern];
        const seen = new Set<number>();
        const unique = combined.filter(item => {
          if (seen.has(item.cardBlueprintId)) return false;
          seen.add(item.cardBlueprintId);
          return true;
        });
        const fallback = isMockMode() ? MOCK_FEATURED_CARDS : [];
        setAllCards(unique.length > 0 ? unique : fallback);

        // Initialize quantities for all items
        const cards = unique.length > 0 ? unique : fallback;
        const initialQuantities: Record<number, number> = {};
        cards.forEach(item => {
          initialQuantities[item.buylistID] = 1;
        });
        setQuantities(initialQuantities);
      } catch (error) {
        console.error('Failed to load featured cards', error);
        const fallbackCards = isMockMode() ? MOCK_FEATURED_CARDS : [];
        setAllCards(fallbackCards);
        const initialQuantities: Record<number, number> = {};
        fallbackCards.forEach(item => {
          initialQuantities[item.buylistID] = 1;
        });
        setQuantities(initialQuantities);
      } finally {
        setLoading(false);
      }
    };
    loadAll();
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollButtons();
    el.addEventListener('scroll', updateScrollButtons, { passive: true });
    window.addEventListener('resize', updateScrollButtons);
    return () => {
      el.removeEventListener('scroll', updateScrollButtons);
      window.removeEventListener('resize', updateScrollButtons);
    };
  }, [allCards, updateScrollButtons]);

  const handleQuantityChange = useCallback((itemId: number, value: number) => {
    setQuantities(prev => ({
      ...prev,
      [itemId]: Math.max(1, value),
    }));
  }, []);

  const handleAddToBuylist = useCallback((item: BuylistItem) => {
    const quantity = quantities[item.buylistID] || 1;
    dispatch(addToBuylist({ item, quantity }));
  }, [dispatch, quantities]);

  // Hide the whole section when there is nothing to show (e.g. the prerender
  // build, where API calls are blocked) instead of rendering an empty state.
  if (!loading && allCards.length === 0) {
    return null;
  }

  return (
    <AnimatedSection>
      <FeaturedSection>
        <FeaturedContainer>
          <FeaturedHeader>
            <FeaturedTitle>Utvalgte kort</FeaturedTitle>
            <SeeAllLink to="/buylist">
              Se alle &rarr;
            </SeeAllLink>
          </FeaturedHeader>
          <FeaturedSubtitle>De mest etterspurte kortene vi kjøper</FeaturedSubtitle>

          {loading ? (
            <LoadingText>Laster utvalgte kort...</LoadingText>
          ) : (
            <StaggerContainer>
              <ScrollWrapper>
                <ScrollIndicator $direction="left" $visible={canScrollLeft} />
                <ScrollIndicator $direction="right" $visible={canScrollRight} />
                <NavButton
                  $direction="left"
                  onClick={() => scroll('left')}
                  disabled={!canScrollLeft}
                  aria-label="Bla til venstre"
                >
                  &#8249;
                </NavButton>
                <ScrollContainer ref={scrollRef}>
                  {allCards.map(item => (
                    <GridStaggerItem key={item.buylistID}>
                      <ScrollItem>
                        <BuylistItemCard
                          item={item}
                          quantity={quantities[item.buylistID] || 1}
                          onQuantityChange={handleQuantityChange}
                          onAddToBuylist={handleAddToBuylist}
                        />
                      </ScrollItem>
                    </GridStaggerItem>
                  ))}
                </ScrollContainer>
                <NavButton
                  $direction="right"
                  onClick={() => scroll('right')}
                  disabled={!canScrollRight}
                  aria-label="Bla til h&#248;yre"
                >
                  &#8250;
                </NavButton>
              </ScrollWrapper>
            </StaggerContainer>
          )}
        </FeaturedContainer>
      </FeaturedSection>
    </AnimatedSection>
  );
};

export default FeaturedCards;
